/**
 * AuthContext — JWT authentication context for the coaching dashboard.
 *
 * Provides: user object, loading state, login/register/logout methods.
 * Validates stored token on mount via /api/auth/me.
 * Handles Microsoft SSO ?token= redirect from OAuth callback.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import config from '../../utils/config';
import { getToken, setToken, clearToken } from '../../utils/apiFetch';

const AuthContext = createContext(null);

/**
 * Login/register responses must be JSON. HTML usually means: wrong URL, backend down,
 * or the SPA dev server returned index.html for /api/* (proxy not configured).
 */
async function parseAuthResponseJson(res) {
  const text = await res.text();
  const start = text.trimStart();
  if (!start) {
    throw new Error(`Empty response from server (${res.status}). Is the API running?`);
  }
  if (start.startsWith('<')) {
    throw new Error(
      'Could not reach the login API — the server sent a web page instead of data. '
      + 'In development, start the backend (port 4000), e.g. `npm run dev` or `npm run server`. '
      + 'In production, confirm the app is built with the correct API base URL.',
    );
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Invalid response from server. Try again or contact support if this persists.');
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Validate an existing token on mount
  useEffect(() => {
    // Check for SSO token in URL first (?token=... from Microsoft callback)
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('token');
    const authError = params.get('auth_error');

    if (authError) {
      // Clean URL and let login page handle the error
      window.history.replaceState({}, '', window.location.pathname);
      setLoading(false);
      return;
    }

    if (ssoToken) {
      setToken(ssoToken);
      // Clean the token from URL immediately
      window.history.replaceState({}, '', window.location.pathname);
    }

    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    // Validate token with backend
    // Uses raw fetch (not apiFetch) to avoid 401-redirect loops during token validation
    fetch(`${config.API_BASE_URL}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Invalid token');
        return res.json();
      })
      .then((data) => {
        if (data.success && data.user) {
          setUser(data.user);
        } else {
          clearToken();
        }
      })
      .catch(() => {
        clearToken();
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${config.API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await parseAuthResponseJson(res);

    if (!res.ok || !data.success) {
      if (res.status === 429) {
        const wait = data.retryAfter ? ` Try again in ${Math.ceil(data.retryAfter / 60)} minute(s).` : '';
        throw new Error(`Too many login attempts.${wait}`);
      }
      throw new Error(data.message || data.error || 'Login failed');
    }

    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password) => {
    const res = await fetch(`${config.API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await parseAuthResponseJson(res);

    if (!res.ok || !data.success) {
      if (res.status === 429) {
        const wait = data.retryAfter ? ` Try again in ${Math.ceil(data.retryAfter / 60)} minute(s).` : '';
        throw new Error(`Too many attempts.${wait}`);
      }
      throw new Error(data.message || data.error || 'Registration failed');
    }

    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      return null;
    }
    const res = await fetch(`${config.API_BASE_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success || !data.user) {
      return null;
    }
    setUser(data.user);
    return data.user;
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
  }), [user, loading, login, register, logout, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
